define(["./DirectSender", "./MatlabSender", "./StubSender", "mw-log/Log"], function (DirectSender, MatlabSender, StubSender, Log) {
  "use strict";

  var _exports = {};
  /**
   *  <p>Defines an API for logging product usage as part of the Data Driven User Experience (DDUX) program.
   *  There are three primary logging methods, each targeting a distinct use case. They are:</P>
   *
   *  <ol>
   *      <li>logData - used for general-purpose logging of key-value pairs</li>
   *      <li>logEvent - used to log user interactions with the UI, such as clicks and key presses</li>
   *      <li>logComposition - used to log the composition of user-produced artifacts such as live editor scripts or Simulink models.</li>
   *   </ol>
   *
   *   <p>The UsageLogger can be utilized in either a standalone web application or a web application backed by
   *   a server-side MATLAB.  In the first case logged data is sent directly to a DDUX server, while in
   *   the second it is sent to the backend MATLAB which will relay it to the DDUX server.  Refer to the
   *   "start" method for details on how to configure the UsageLogger for a particular web application.</p>
   *
   * <p><b>IMPORTANT</b></P>
   *
   * <p>Before adding logging for a product or extending existing logging you must submit a "DDUX data collection proposal"
   * via the MathWorks Proposal Center and obtain approval.</p>
   *
   * @copyright 2018-2024 The MathWorks, Inc.
   * @module mw-ddux/UsageLogger
   */

  let _sender;
  let _uiEventDefaults;
  let _telemetryDefaults;
  let _compositionDefaults;
  let _selectedContext;
  const _elementIdPrefixes = {};
  const _taps = [];
  const hasWindow = function () {
    return typeof window !== 'undefined';
  };
  const UsageLogger = {
    _isRunning: false,
    allowProductScopeOverride: false,
    /**
     * Initializes usage logging.  Logging calls made before start are dropped.
     *
     * @function start
     * @static
     *
     * @param loggerProperties {object} supplies properties that control the behavior of the logger.
     *        These can include default values for various event identification attributes.
     * @param loggerProperties.product {string} identifies the product for all UI event and
     *        telemetry logs unless overridden
     * @param loggerProperties.scope {string} identifies the scope for all UI event logs
     *        unless overridden.
     * @param loggerProperties.appComponent {string} identifies the appComponent for all
     *        telemetry logs unless overridden.  "scope" and "appComponent" play a similar roll
     *        in UI event logs and telemetry logs, respectively.  If the same value should be used
     *        for both you may specify either rather than both.
     * @param loggerProperties.eventKey {string} identifies the telemetry stream for all
     *        telemetry logs unless overridden
     * @param loggerProperties.artifactType {string} identifies the type of artifact associated with all
     *        artifact composition logs unless overridden.
     * @param loggerProperties.allowProductScopeOverride {boolean} should be set true if
     *        products and scopes other than those specified above can exist in the same window.
     *        In this case the UsageLogger will look for annotations on the DOM that can be used
     *        to override the default product and scope.
     * @param loggerProperties.dontSend {boolean} true if logged data should not be sent (intented for testing)
     * @param loggerProperties.sendDirect {boolean} true if logged data should be sent directly to the DDUX
     *        server rather than via a MATLAB backend.
     * @param loggerProperties.applicationName {string} the name of the application logging data
     * @param loggerProperties.applicationVersion {string} the version of the application logging data
     * @param loggerProperties.sendToDev when sending directly to the DDUX server, specifies data should go to
     *        the development (dev) or production (prod) URL. If not specified data goes to prod if the
     *        page hostname is "mathworks.com" (optional)
     * @param loggerProperties.release {string} The mathworks product release such as 'R2020b'.  If specified
     *        the release is used to determine the dev endpoint for direct send. (optional)
     * @param loggerProperties.endPointNumber {number} 1, 2 or 3 indicating which "integ" endpoint to send to.
     *        If specified this overrides the release-based dev endpoint selection (optional)
     * @param LoggerProperties.endPointUrl {string} URL to which logged data should be posted when sending
     *        directly to the DDUX sercer (optional). Takes precedence over other properties that contribute to
     *        determining the end point URL including "sendToDev", "release" and "endPointNumber"
     */
    start: function (loggerProperties) {
      if (this._isRunning) {
        return;
      }
      this._isRunning = true;
      if (hasWindow()) {
        window.UsageLogger = this;
      }
      loggerProperties = loggerProperties || {};
      _uiEventDefaults = {
        product: loggerProperties.product,
        scope: loggerProperties.scope || loggerProperties.appComponent
      };
      _telemetryDefaults = {
        product: loggerProperties.product,
        appComponent: loggerProperties.appComponent || loggerProperties.scope,
        eventKey: loggerProperties.eventKey
      };
      _compositionDefaults = {
        product: loggerProperties.product,
        appComponent: loggerProperties.appComponent || loggerProperties.scope,
        artifactType: loggerProperties.artifactType,
        artifactId: loggerProperties.artifactId
      };
      this.allowProductScopeOverride = loggerProperties.allowProductScopeOverride;
      _sender = loggerProperties.dontSend ? new StubSender() : loggerProperties.sendDirect ? new DirectSender(loggerProperties) : new MatlabSender(loggerProperties);
    },
    /**
     * Terminates usage logging.  Logging calls made after stop are dropped.
     *
     * @function stop
     * @static
     */
    stop: function () {
      if (this._isRunning) {
        this._isRunning = false;
        _sender.stop();
        _sender = undefined;
      }
    },
    /**
     * Indicates whether the usage logger is running, i.e. has been started but not stopped.
     *
     * @function isRunning
     * @static
     * @returns {boolean} true if running
     */
    isRunning: function () {
      return this._isRunning;
    },
    /**
     * Sets default properties applied when logging UI events, data or artifact composition, overriding
     * any values specified upon start.
     *
     * @function setDefaults
     * @static
     * @param updatedProperties {object} replacement property values
     * @param loggerProperties.product {string}
     * @param loggerProperties.scope {string}
     * @param loggerProperties.appComponent {string}
     * @param loggerProperties.eventKey {string}
     * @param loggerProperties.artifactType {string}
     */
    setDefaults: function (updatedProperties) {
      _uiEventDefaults.product = updatedProperties.product || _uiEventDefaults.product;
      _uiEventDefaults.scope = updatedProperties.scope || updatedProperties.appComponent || _uiEventDefaults.scope;
      _telemetryDefaults.product = updatedProperties.product || _telemetryDefaults.product;
      _telemetryDefaults.appComponent = updatedProperties.scope || updatedProperties.appComponent || _telemetryDefaults.appComponent;
      _telemetryDefaults.eventKey = updatedProperties.eventKey || _telemetryDefaults.eventKey;
      _compositionDefaults.product = updatedProperties.product || _compositionDefaults.product;
      _compositionDefaults.appComponent = updatedProperties.scope || updatedProperties.appComponent || _compositionDefaults.appComponent;
      _compositionDefaults.artifactType = updatedProperties.artifactType || _compositionDefaults.artifactType;
      _compositionDefaults.artifactId = updatedProperties.artifactId || _compositionDefaults.artifactId;
    },
    /**
     * Logs arbitrary key-value pairs.  This is the most general purpose logging method.
     *
     * @function logData
     * @static
     * @param data {object} arbitrary key value pairs to be logged
     * @param identification {object} optional properties that identify the data source, overriding the
     *        defaults specified via start
     * @param identification.product {string} identifies the product with which the data is associated
     * @param identification.appComponent {string} identifies the app component with which the data is associated
     * @param identification.eventKey {string} identifies the data stream
     */
    logData: function (data, identification) {
      if (!this._isRunning) {
        const ancestorLogger = this._getRunningAncestor();
        if (ancestorLogger) {
          ancestorLogger.logData(data, identification);
          return;
        } else if (!(hasWindow() && window.UsageLogger)) {
          Log.info('UsageLogger start must be called before logging data');
          return;
        }
      }
      // Defer so that if this event was triggered by a UI click that click will be sent first
      const that = this;
      const doSend = function () {
        that._sendEvent('telemetry', that._composeTelemetryEvent(data, identification));
      };
      if (_taps.length > 0) {
        doSend();
      } else {
        setTimeout(doSend, 0);
      }
    },
    /**
     * Logs an arbitrary by converting it to JSON. This method is intended only for matching
     * legacy logging format. All new logging should use logData, logEvent or logComposition.
     *
     * Also note that logCustomData only works when "endDirect: true has been specified
     * upon UsageLogger start.
     *
     * @function logCustomData
     * @static
     * @param data {object} arbitrary object (must be convertable to JSON)
     * @param identification {object} optional properties that identify the data source, overriding the
     *        defaults specified via start
     * @param identification.appComponent {string} identifies the app component with which the data is associated
     * @param identification.eventKey {string} identifies the data stream
     */
    logCustomData: function (data, identification) {
      if (!this._isRunning) {
        const ancestorLogger = this._getRunningAncestor();
        if (ancestorLogger) {
          ancestorLogger.logCustomData(data, identification);
          return;
        } else if (!(hasWindow() && window.UsageLogger)) {
          Log.info('UsageLogger start must be called before logging data');
          return;
        }
      }
      // Defer so that if this event was triggered by a UI click that click will be sent first
      const that = this;
      const doSend = function () {
        that._sendEvent('custom', that._composeCustomEvent(data, identification));
      };
      if (_taps.length > 0) {
        doSend();
      } else {
        setTimeout(doSend, 0);
      }
    },
    /**
     * Logs a UI event
     *
     * @function logUIEvent
     * @static
     * @param eventData {object} an object that contains at a minimum eventType and elementId properties but may also contain elementType and additional string-valued properties
     * @param eventData.eventType {string} identifies the type of event.  Can be any of the following:
     *   "cell_edit",
     *   "clicked",
     *   "closed",
     *   "column_description_changed",
     *   "column_dragged",
     *   "column_sorted",
     *   "deselected",
     *   "docked",
     *   "double_clicked",
     *   "dragged",
     *   "key_pressed",
     *   "location_changed",
     *   "maximized",
     *   "menu_item_selected",
     *   "minimized",
     *   "opened",
     *   "restored",
     *   "scrolled",
     *   "selected",
     *   "selection_changed",
     *   "undocked",
     *   "units_changed",
     *   "variable_opened",
     * @param eventData.elementId {string} identifies the UI element the user interacted with
     * @param eventData.elementType {string} identifies the type of UI element the user interactive with.  Can be any of the following:
     *   "app",
     *   "button",
     *   "combo_box",
     *   "document",
     *   "document_group",
     *   "drop_down",
     *   "list",
     *   "list_item",
     *   "menu",
     *   "menu_item",
     *   "slider",
     *   "spinner",
     *   "table",
     *   "text field",
     *   "text area",
     *   "panel",
     *   "toolstrip",
     *   "toolstrip tab"
     */
    logUIEvent: function (eventData) {
      if (!this._isRunning) {
        const ancestorLogger = this._getRunningAncestor();
        if (ancestorLogger) {
          ancestorLogger.logEvent(eventData);
          return;
        } else if (!(hasWindow() && window.UsageLogger)) {
          Log.info('UsageLogger start must be called before logging data');
          return;
        }
      }
      Log.assert(eventData.eventType, 'eventType must be defined');
      if (eventData.elementType) {
        if (!eventData.elementId) {
          Log.warn('Can\'t log ' + eventData.elementType + ' ' + eventData.eventType + ' without elementId');
          return;
        }
      }
      eventData.product = eventData.product || _uiEventDefaults.product;
      eventData.scope = eventData.scope || _uiEventDefaults.scope;
      const that = this;
      const doLogEvent = function () {
        let attributes;
        try {
          that._filterElementId(eventData);
          // Include the selected context if the event is a keypress or a toolstrip event
          if (_selectedContext && (eventData.eventType === 'keypress' || attributes && attributes.subscope === 'toolstrip')) {
            eventData.selection = _selectedContext;
          }
          that._sendEvent('uiEvent', eventData);
        } catch (error) {
          Log.warn('Error while logging event: ' + error.message);
        }
        that._uiEventInProgress = false;
      };
      // Defer event processing of events other than clicks. The intent is to have events triggered by a click follow
      // the click event itself
      if (eventData.eventType === 'click' || _taps.length > 0) {
        doLogEvent();
      } else {
        this._uiEventInProgress = true;
        setTimeout(doLogEvent, 0);
      }
    },
    /**
     * Alias for LogUIEvent
     * @param eventData
     */
    logEvent: function (eventData) {
      this.logUIEvent(eventData);
    },
    /**
     * Specifies a context that may be helpful in interpreting keyboard or toolstrip UI events.
     * For example the caller could provide the id associated with the selected region of the UI.
     * This id will be included in subsequent UI event logs as the "selected" field when relevant.
     *
     * @function setSelectedContext
     * @static
     * @param context
     */
    setSelectedContext: function (context) {
      _selectedContext = context;
    },
    setPrefixesOfElementIdsToRedact: function (prefixes, product, scope) {
      _elementIdPrefixes[this._productAndScopeKey(product || _uiEventDefaults.product, scope || _uiEventDefaults.scope)] = prefixes;
    },
    /**
     * Logs artifact composition.
     *
     * @function logComposition
     * @static
     * @param identification {object} properties that identify the data source and artifact, overriding the
     *        defaults, if any, specified via start
     * @param identification.product {string} identifies the product with which the artifact is associated
     * @param identification.appComponent {string} identifies the app component with which the artifact is associated
     * @param identification.artifactType {string} indicated the type of artifact
     * @param identification.artifactId {string} uniquely identifies the artifact.  This string will be hashed so that any
     *        contained IP or PII will be obscured.
     * @param properties {object} artifact property names and values to be logged.  The values may be string, boolean or number
     * @param componentCounts {object} artifact component types and corresponding non-zero counts
     * @param componentProperties {array} of objects, each conmtaining:
     * @param componentProperties.componentType
     * @param componentProperties.propertyName
     * @param componentProperties.propertyValue
     */
    logComposition: function (identification, properties, componentCounts, componentProperties) {
      if (!this._isRunning) {
        const ancestorLogger = this._getRunningAncestor();
        if (ancestorLogger) {
          ancestorLogger.logComposition(identification, properties, componentCounts, componentProperties);
          return;
        } else if (!(hasWindow() && window.UsageLogger)) {
          Log.info('UsageLogger start must be called before logging data');
          return;
        }
      }
      identification = identification || {};
      // Compose event
      const event = {
        product: identification.product || _compositionDefaults.product,
        appComponent: identification.appComponent || _compositionDefaults.appComponent,
        artifactType: identification.artifactType || _compositionDefaults.artifactType,
        artifactId: identification.artifactId || _compositionDefaults.artifactId,
        properties: properties,
        componentCounts: componentCounts,
        componentProperties: componentProperties
      };
      Log.assert(event.product, 'product must be defined');
      Log.assert(event.appComponent, 'appComponent must be defined');
      Log.assert(event.artifactType, 'artifactType must be defined');
      Log.assert(event.artifactId, 'artifactId must be defined');

      // Defer so that if this event was triggered by a UI click that click will be sent first
      const that = this;
      const doSend = function () {
        that._sendEvent('composition', event);
      };
      if (_taps.length > 0) {
        doSend();
      } else {
        setTimeout(doSend, 0);
      }
    },
    /**
     * Supports "taping", i.e. listening in on, the ddux log stream for test.
     *
     * @function addTap
     * @static
     * @param matchProperties {object} determines which ddux logs are sent to the given tapFunction.
     *        matchProperties can contain any set of properties and values (including empty) that may
     *        appear in the ddux logs.  Logs matching all the specified properties will be passed to the
     *        tapFunction.  If matchProperties is empty then all logs will be passed.  An example
     *        matchProperties value might be {product: "Image Processing Toolbox", scope: "Camera Calibrator"}.
     *
     *        Note taps added using this method only "see" events logged via the same UsageLogger.
     *        If you need to see all DDUX events logged within MATLAB you'll need to use either the MATLAB
     *        or C++ add tap capabilities.
     *
     * @param tapFunction {function (object)} function to which matching logs will be passed.
     * @param blockDefault {boolean} if true then data sent to the tap function will not be sent on down the pipe.
     * @return an object whose remove() function can be called to remove the tap
     */
    addTap: function (matchProperties, tapFunction, blockDefault) {
      const tap = {
        matchProperties: matchProperties,
        tapFunction: tapFunction,
        blockDefault: blockDefault
      };
      _taps.push(tap);
      return {
        remove: function () {
          const index = _taps.indexOf(tap);
          if (index >= 0) {
            _taps.splice(index, 1);
          }
        }
      };
    },
    _composeTelemetryEvent: function (data, identification) {
      identification = identification || {};
      const event = {
        product: identification.product || _telemetryDefaults.product,
        appComponent: identification.appComponent || _telemetryDefaults.appComponent,
        eventKey: identification.eventKey || _telemetryDefaults.eventKey,
        data: data
      };
      Log.assert(event.eventKey, 'eventKey must be defined');
      return event;
    },
    _composeCustomEvent: function (data, identification) {
      identification = identification || {};
      const event = {
        appComponent: identification.appComponent || _telemetryDefaults.appComponent,
        eventKey: identification.eventKey || _telemetryDefaults.eventKey,
        data: data
      };
      Log.assert(event.eventKey, 'eventKey must be defined');
      return event;
    },
    _sendEvent: function (eventType, event) {
      if (!this._isRunning) {
        // May have stopped. See geck 3415256.
        return;
      }
      // Send to matching taps
      let blocked = false;
      _taps.forEach(function (tap) {
        const matchKeys = Object.keys(tap.matchProperties);
        let allMatch = true;
        for (let k = 0; k < matchKeys.length && allMatch; k++) {
          const key = matchKeys[k];
          allMatch = tap.matchProperties[key] === event[key];
        }
        if (allMatch) {
          tap.tapFunction(event);
          if (tap.blockDefault) {
            blocked = true;
          }
        }
      });
      if (!blocked) {
        _sender.sendEvent(eventType, event);
      }
    },
    _getRunningAncestor: function () {
      if (hasWindow()) {
        let ancestor = window.opener;
        while (ancestor) {
          const ancestorLogger = ancestor.UsageLogger;
          if (ancestorLogger && ancestorLogger._isRunning) {
            return ancestorLogger;
          }
          ancestor = ancestor.opener;
        }
      }
      return null;
    },
    _filterElementId: function (eventData) {
      const product = eventData.product || _uiEventDefaults.product;
      const scope = eventData.scope || _uiEventDefaults.scope;
      const prefixes = _elementIdPrefixes[this._productAndScopeKey(product, scope)];
      const isNumber = function (s) {
        return !/\D/.test(s);
      };
      if (prefixes) {
        let i, prefix;
        for (i = 0; i < prefixes.length; i++) {
          prefix = prefixes[i];
          if (eventData.elementId.startsWith(prefix)) {
            const remainder = eventData.elementId.slice(prefix.length);
            eventData.elementId = prefix + ':redacted';
            if (remainder.length > 0) {
              const c = remainder.charAt(0);
              if (c === ':' || c === '.') {
                eventData.redactedHash = this.hashString(remainder);
              } else if (isNumber(remainder)) {
                eventData.redactedHash = remainder;
              }
            }
            break;
          }
        }
      }
    },
    _productAndScopeKey: function (product, scope) {
      return product + ':' + scope;
    },
    // TODO: replace with something standard like MD5.
    hashString: function (s) {
      let hash = 0;
      for (let i = 0; i < s.length; i++) {
        hash += Math.pow(s.charCodeAt(i) * 31, s.length - i);
        hash &= hash;
      }
      return Math.abs(hash).toString(16);
    }
  };
  _exports.default = UsageLogger;
  return _exports.default;
});
//# sourceMappingURL=UsageLogger.js.map
